import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import RadioGroup from '@mui/material/RadioGroup'
import { useFormContext, useFieldArray } from 'react-hook-form'

import AddIcon from '@mui/icons-material/Add'

import { FlexColumn, CenteredRowContainer, LightBGContainer } from '../styles/Containers'
import { SecondaryButton } from '../common/Button'
import { RowFormControl, StyledFormControlLabel, StyledRadio } from '../styles/RadioGroup'
import { formSection, formWording } from '../styles/FormStyleConfig'
import { Subheading } from '../styles/Text'
import PropertyAddressForm from './PropertyAddressForm'

const emptyAddress = {
  legalDescription: '',
  address: '',
  unit: '',
  city: '',
  province: '',
  postalCode: '',
  files: []
}

function PropertyAddresses({ format = 'default', perspective = 'requestor', disabled = false }) {
  const { fields, append, remove } = useFieldArray({ name: 'addresses' })

  const { register, watch, getValues } = useFormContext()
  const [defaultHasAddresses, setDefaultHasAddresses] = useState()

  useEffect(() => {
    const defaultVal = getValues('hasAddresses') !== undefined ? getValues('hasAddresses') : null
    setDefaultHasAddresses(defaultVal)
  }, [])

  const hasAddresses = watch('hasAddresses')

  useEffect(() => {
    if (hasAddresses && fields.length === 0) {
      append(emptyAddress)
    }
  }, [hasAddresses])

  const TextComponent = formSection[format].font.bodyText

  return (
    <FlexColumn gap={formSection[format].gap.small}>
      <Subheading>Property Addresses</Subheading>
      {defaultHasAddresses !== undefined && (
        <FlexColumn gap="0">
          <RowFormControl>
            <TextComponent>Is {formWording[perspective].possessive} request associated with one or more addresses?</TextComponent>
            <RadioGroup row name="has-addresses-radio-group" id="hasAddresses" defaultValue={defaultHasAddresses}>
              <StyledFormControlLabel
                {...register('hasAddresses')}
                value={true}
                control={<StyledRadio />}
                label="Yes"
                format={format}
                disabled={disabled}
              />
              <StyledFormControlLabel
                {...register('hasAddresses')}
                value={false}
                control={<StyledRadio />}
                label="No"
                format={format}
                disabled={disabled}
              />
            </RadioGroup>
          </RowFormControl>
        </FlexColumn>
      )}
      {hasAddresses && hasAddresses.toString() === 'true' && (
        <FlexColumn gap={formSection[format].gap.subDiv}>
          <TextComponent>Please add the associated address(es) below: </TextComponent>
          {fields.map((field, index) => (
            <LightBGContainer key={field.id} padding={formSection[format].padding.subsection}>
              <PropertyAddressForm
                sectionTitle={`Address ${index + 1}`}
                id={`addresses.${index}`}
                onDelete={() => remove(index)}
                showDelete={!(fields.length === 1 && index === 0)}
                format={format}
                disabled={disabled}
              />
            </LightBGContainer>
          ))}
          <CenteredRowContainer>
            <SecondaryButton onClick={() => append(emptyAddress)} disabled={disabled}>
              <AddIcon />
              Add Address
            </SecondaryButton>
          </CenteredRowContainer>
        </FlexColumn>
      )}
    </FlexColumn>
  )
}

PropertyAddresses.propTypes = {
  format: PropTypes.oneOf(['default', 'condensed']),
  perspective: PropTypes.oneOf(['requestor', 'staff']),
  disabled: PropTypes.bool
}

export default PropertyAddresses
